<template>
    <modal :title="$t('account_redesign.edit_uam_account')" :visible.sync="isShowDialog" width="1200px" :close-on-click-modal="false">
        <el-form ref="editForm" class="edit-content" label-width="160px" :rules="editRules" :model="dioForm">
            <!-- 基本信息（左列） -->
            <div class="leftContent">
                <el-form-item :label="$t('user.account')">
                    <span>{{ dioForm.account }}</span>
                </el-form-item>
                <el-form-item :label="$t('account_redesign.display_name')" prop="username">
                    <el-input v-model.trim="dioForm.username" style="width: 300px"></el-input>
                </el-form-item>
                <el-form-item :label="$t('public.phone_num')" prop="mobile">
                    <el-input v-model.trim="dioForm.mobile" style="width: 300px"></el-input>
                </el-form-item>
                <el-form-item :label="$t('public.email')" prop="email">
                    <el-input v-model.trim="dioForm.email" style="width: 300px"></el-input>
                </el-form-item>
                <el-form-item v-if="showCompanyField" :label="$t('account_redesign.company')">
                    <span>{{ companyDisplayName }}</span>
                </el-form-item>
            </div>
            <!-- Role 和 Scope 同行 -->
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item :label="$t('account_redesign.role')" prop="roleId">
                        <div class="select-with-refresh">
                            <el-select v-model="dioForm.roleId" filterable style="width:300px" @change="onRoleChange">
                                <el-option v-for="item in roleArray" :key="item.id" :value="item.id" :label="item.name"></el-option>
                            </el-select>
                            <el-button circle icon="el-icon-refresh" size="mini" @click="refreshRoles" :loading="roleLoading"></el-button>
                        </div>
                        <div class="link-wrapper">
                            <router-link :to="{ path: '/account/role' }" target="_blank" class="add-link">{{ $t('account_redesign.no_role_add') }}</router-link>
                        </div>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item v-if="!isVmark" :label="$t('account_redesign.scope')">
                        <div class="select-with-refresh">
                            <el-select v-model="dioForm.scopeId" filterable clearable style="width:300px" @change="onScopeChange">
                                <el-option v-for="item in scopeArray" :key="item.id" :value="item.id" :label="item.name"></el-option>
                            </el-select>
                            <el-button circle icon="el-icon-refresh" size="mini" @click="refreshScopes" :loading="scopeLoading"></el-button>
                        </div>
                        <div class="link-wrapper">
                            <router-link :to="{ path: '/account/scope' }" target="_blank" class="add-link">{{ $t('account_redesign.no_scope_add') }}</router-link>
                        </div>
                    </el-form-item>
                </el-col>
            </el-row>
            <!-- Role 权限树 和 Organization 同行 -->
            <el-row :gutter="20" v-if="roleResourceTree.length > 0 || showTree">
                <el-col :span="12">
                    <el-form-item v-if="roleResourceTree.length > 0" :label="$t('account_redesign.role_permission')">
                        <div class="treeClass role-tree" v-loading="roleTreeLoading">
                            <el-tree :key="$i18n.locale" :props="props" :data="roleResourceTree" :default-expand-all="true" node-key="id" :render-content="renderRoleTreeContent"></el-tree>
                        </div>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item v-if="showTree" :label="$t('account_redesign.organization')">
                        <div class="treeClass" v-loading="loadingTree">
                            <el-tree :props="props" :data="displayTree" :default-expand-all="true" node-key="scene_id"></el-tree>
                        </div>
                    </el-form-item>
                </el-col>
            </el-row>
        </el-form>
        <div slot="footer" class="dialog-footer-gap">
            <el-button type="primary" @click="isShowDialog = false">{{ $t('account_redesign.close') }}</el-button>
            <el-button type="primary" :loading="saving" @click="handleSave">{{ $t('public.btn_apply') }}</el-button>
        </div>
    </modal>
</template>

<script>
import { accountCustomer, accountRole, accountStructure } from '@/api/modules/userInfo.js';
import { listScopes, updateSubAccount } from '@/api/modules/account.js';
import { roleResource } from '@/api/modules/role.js';

export default {
    props: ['isShow', 'editRow'],
    computed: {
        isShowDialog: {
            get() {
                return this.isShow;
            },
            set(val) {
                this.$emit('update:isShow', val);
            }
        },
        editRules() {
            return {
                account: [
                    { required: true, message: this.$t('validate.user_account_required'), trigger: 'blur' },
                    { max: 20, message: this.$t('validate.len_20'), trigger: 'blur' }
                ],
                username: [
                    { required: true, message: this.$t('validate.user_name_required'), trigger: 'blur' },
                    { max: 50, message: this.$t('validate.len_50'), trigger: 'blur' }
                ],
                mobile: [
                    { required: true, message: this.$t('validate.user_mobile_required'), trigger: 'blur' },
                    { max: 20, message: this.$t('validate.len_20'), trigger: 'blur' }
                ],
                email: [
                    { required: true, message: this.$t('validate.email_required'), trigger: 'blur' },
                    { max: 255, message: this.$t('validate.len_255'), trigger: 'blur' },
                    { type: 'email', message: this.$t('validate.email_format'), trigger: ['blur', 'change'] }
                ],
                roleId: [{ required: true, message: this.$t('validate.user_role_required'), trigger: 'change' }]
            };
        },
        // 当前登录用户是否平台企业（仅用于判断是否显示 Company 下拉）
        isPlatformUser() {
            const userInfo = this.$store.getters.userInfo;
            if (!userInfo) return false;
            // 兼容 snake_case / camelCase，优先从 scope 取，fallback 到 user.default_customer_id
            const scope = userInfo.scope || {};
            const user = userInfo.user || {};
            const cid = scope.customerId
                || user.defaultCustomerId || '';
            return String(cid) === '1';
        },
        showCompanyField() {
            // 平台企业管理多企业账号时才显示 Company 下拉
            return this.isPlatformUser;
        },
        // 被编辑账号的 customerId：从列表行数据(editRow)获取，不从 userInfo 拿
        currentCompanyId() {
            if (!this.editRow) return '';
            return String(this.editRow.customerId || '');
        },
        isVmark() {
            return String(this.currentCompanyId) === '1';
        },
        calcRoleType() {
            return this.isVmark ? 2 : 0;
        },
        companyDisplayName() {
            if (!this.showCompanyField) return '';
            const c = this.customerArray.find(item => String(item.id) === String(this.currentCompanyId));
            return c ? c.name : (this.editRow ? (this.editRow.customerName || '') : '');
        },
        showTree() {
            // Vmark (Company id==1) 显示 Organization；非 Vmark 时需选择 Scope 才显示
            return this.isVmark || (this.dioForm.scopeId !== '' && this.dioForm.scopeId != null);
        },
        displayTree() {
            if (this.isVmark) {
                return this.fullTree;
            }
            if (this.dioForm.scopeId) {
                const scope = this.scopeArray.find(s => s.id === this.dioForm.scopeId);
                if (scope) {
                    // 后端 buildSceneTrees 已为每个 scope 填充 scene_tree（完整企业组织架构树）
                    return scope.sceneTree || [];
                }
            }
            return [];
        }
    },
    watch: {
        isShow(val) {
            if (val) {
                this.initData();
            }
        },
        '$i18n.locale'() {
            // 切换语言时重新加载角色权限树，使树标签跟随当前语言
            if (this.roleResourceTree.length > 0 && this.dioForm.roleId) {
                this.loadRoleResource();
            }
        }
    },
    data() {
        return {
            dioForm: {
                id: null,
                username: '',
                account: '',
                mobile: '',
                email: '',
                customerId: '',
                roleId: '',
                scopeId: '',
                sceneIds: []
            },
            customerArray: [],
            roleArray: [],
            scopeArray: [],
            fullTree: [],
            roleLoading: false,
            scopeLoading: false,
            loadingTree: false,
            roleResourceTree: [],
            roleTreeLoading: false,
            saving: false,
            props: {
                label: 'label',
                children: 'item'
            }
        };
    },
    methods: {
        renderRoleTreeContent() {
            let createElement = arguments[0]
            let data = arguments[1].data
            let isZh = (this.$i18n.locale || '').indexOf('zh') === 0
            let displayName = isZh ? (data.nameZh || data.label) : (data.nameEn || data.label)
            return createElement('span', displayName)
        },
        async initData() {
            const row = this.editRow;
            if (!row) return;
            this.dioForm = {
                id: row.id,
                username: row.username || '',
                account: row.account || '',
                mobile: row.mobile || '',
                email: row.email || '',
                status: row.status,
                // customerId 直接取自列表行数据
                customerId: String(row.customerId || ''),
                roleId: row.roleId || '',
                scopeId: row.scopeId || '',
                sceneIds: []
            };
            this.customerArray = [];
            this.roleArray = [];
            this.scopeArray = [];
            this.fullTree = [];
            this.roleResourceTree = [];

            // 加载 Company 下拉（仅 showCompanyField 时）
            if (this.showCompanyField) {
                await this.loadCustomers();
            }
            // 加载 Role 下拉
            await this.loadRoles();
            // 加载 Scope 下拉 + 组织架构树
            if (this.isVmark) {
                // 平台企业：无 scope，加载完整树
                await this.loadFullTree();
            } else {
                // 非平台企业：scope 自带 scene_tree，不需要单独 loadFullTree
                await this.loadScopes();
            }
            // 如果已有 roleId，加载 Role 权限树
            if (this.dioForm.roleId) {
                this.loadRoleResource();
            }
        },
        async loadCustomers() {
            try {
                const [platRes, entRes] = await Promise.all([
                    accountCustomer({ type: 2 }),
                    accountCustomer({ type: 0 })
                ]);
                const platform = (platRes && platRes.result) || [];
                const enterprises = (entRes && entRes.result) || [];
                this.customerArray = [...platform, ...enterprises];
            } catch (e) {
                console.error('fetch customers error:', e);
            }
        },
        async loadRoles() {
            this.roleLoading = true;
            try {
                const res = await accountRole({
                    type: this.calcRoleType,
                    customerId: this.currentCompanyId || null
                });
                this.roleArray = res.result || [];
            } catch (e) {
                console.error('fetch roles error:', e);
                this.roleArray = [];
            } finally {
                this.roleLoading = false;
            }
        },
        refreshRoles() {
            this.loadRoles();
        },
        /** Role 选择变化时加载权限树 */
        onRoleChange() {
            if (this.dioForm.roleId) {
                this.loadRoleResource();
            } else {
                this.roleResourceTree = [];
            }
        },
        async loadRoleResource() {
            this.roleTreeLoading = true;
            try {
                const res = await roleResource({
                    id: this.dioForm.roleId,
                    type: this.calcRoleType,
                    isEdit: 0
                });
                if (res && res.result) {
                    this.roleResourceTree = res.result.item || [];
                } else {
                    this.roleResourceTree = [];
                }
            } catch (e) {
                console.error('fetch role resource error:', e);
                this.roleResourceTree = [];
            } finally {
                this.roleTreeLoading = false;
            }
        },
        changeCustomer() {
            this.dioForm.roleId = '';
            this.dioForm.scopeId = '';
            this.roleResourceTree = [];
            this.loadRoles();
            if (!this.isVmark) {
                this.loadScopes();
            }
            this.loadFullTree();
        },
        async loadScopes() {
            this.scopeLoading = true;
            try {
                const res = await listScopes({
                    customerId: this.currentCompanyId
                });
                if (res && res.state && res.state.code === 200) {
                    this.scopeArray = res.result || [];
                    // 校验 dioForm.scopeId 是否在 scopeArray 中
                    // row.scopeId 来自后端 SQL 的 IFNULL(s.id, e.id)，实际是 scene.id，
                    // 而 scopeArray 的 id 来自 user_scope 表，两者是不同的 id 空间。
                    // 如果不匹配，自动选中第一个 scope 选项。
                    if (this.dioForm.scopeId !== '' && this.dioForm.scopeId != null) {
                        const match = this.scopeArray.find(s => s.id === this.dioForm.scopeId);
                        if (!match) {
                            this.dioForm.scopeId = this.scopeArray.length > 0 ? this.scopeArray[0].id : '';
                        }
                    } else if (this.scopeArray.length > 0) {
                        this.dioForm.scopeId = this.scopeArray[0].id;
                    }
                }
            } catch (e) {
                console.error('fetch scopes error:', e);
                this.scopeArray = [];
            } finally {
                this.scopeLoading = false;
            }
        },
        refreshScopes() {
            this.loadScopes();
        },
        async loadFullTree() {
            this.loadingTree = true;
            try {
                const res = await accountStructure({
                    type: this.calcRoleType,
                    isEdit: 0,
                    customerId: this.isVmark ? null : (this.currentCompanyId || null)
                });
                this.fullTree = (res && res.result && res.result.item) || [];
            } catch (e) {
                console.error('fetch tree error:', e);
                this.fullTree = [];
            } finally {
                this.loadingTree = false;
            }
        },
        onScopeChange() {
            // 树通过 computed displayTree 自动更新
        },
        parseSceneIds(sceneIdsStr) {
            if (!sceneIdsStr) return [];
            return sceneIdsStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
        },
        filterTreeByIds(nodes, ids) {
            const result = [];
            if (!nodes) return result;
            nodes.forEach(node => {
                const children = this.filterTreeByIds(node.item, ids);
                const isMatch = ids.includes(node.sceneId);
                if (isMatch || children.length > 0) {
                    result.push({ ...node, item: children });
                }
            });
            return result;
        },
        async handleSave() {
            try {
                await this.$refs.editForm.validate();
            } catch (e) {
                return;
            }
            this.saving = true;
            try {
                // 构造 UpdateSubAccountBody 入参（后端从 token 注入 operatorId，前端无需传）
                // 包含基本信息 + 角色 + 范围
                const payload = {
                    userId: this.dioForm.id,
                    displayName: this.dioForm.username,
                    email: this.dioForm.email,
                    mobile: this.dioForm.mobile,
                    roleId: this.dioForm.roleId,
                    scopeIds: this.dioForm.scopeId ? [this.dioForm.scopeId] : []
                };
                const res = await updateSubAccount(payload);
                if (res && res.state && res.state.code === 200) {
                    this.$message.success(this.$t('user.user_edit_code_200'));
                    this.isShowDialog = false;
                    this.$parent.getListFn();
                } else if (res && res.state) {
                    this.$message.error(res.state.msg || 'update failed');
                }
            } catch (e) {
                console.error('edit account error:', e);
                if (e && e.response && e.response.data && e.response.data.state) {
                    this.$message.error(e.response.data.state.msg || 'update failed');
                }
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.edit-content {
    width: 100%;

    .leftContent {
        width: 50%;
    }

    .select-with-refresh {
        display: flex;
        align-items: center;
        gap: 8px;
        ::v-deep .el-select {
            flex-shrink: 0;
        }
    }

    .link-wrapper {
        width: 300px;
        text-align: right;
        box-sizing: border-box;
    }

    .add-link {
        display: inline-block;
        margin-top: 4px;
        font-size: 12px;
        color: var(--primary-color);
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }

    .treeClass {
        max-height: 250px;
        overflow: auto;
        border: 1px solid #e7e7e7;
        padding: 10px 0;
        box-sizing: border-box;
        width: 340px;
    }
}

/* 弹窗底部按钮居中 + 间距 */
.dialog-footer-gap {
    display: flex;
    justify-content: center;
    gap: 12px;
}
</style>
